import React, { useState, useEffect } from "react";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { Box, Paper, Tabs, Tab } from "@mui/material";
import {
  TuneOutlined,
  LabelOutlined,
  SmartToyOutlined,
  CampaignOutlined,
  AdsClickOutlined,
  PhoneInTalkOutlined,
  CalendarMonthOutlined,
  BusinessOutlined,
  WorkspacePremiumOutlined,
  HelpOutlineOutlined,
  HubOutlined,
  SupportAgentOutlined,
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";

import { makeStyles } from 'tss-react/mui';

import TabPanel from "../../components/TabPanel";

import CompaniesManager from "../../components/CompaniesManager";
import PlansManager from "../../components/PlansManager";
import HelpsManager from "../../components/HelpsManager";
import MetaGraphVersionSettings from "../../components/MetaGraphVersionSettings";
import Options from "../../components/Settings/Options";
import ContactStatusManager from "../../components/ContactStatusManager";
import OpenAiAccountSettings from "../../components/OpenAiAccountSettings";
import MetaAdsAccountSettings from "../../components/MetaAdsAccountSettings";
import GoogleAdsSettings from "../../components/GoogleAdsSettings";
import GoogleAdsLinks from "../../components/GoogleAdsLinks";
import GoogleAdsAccount from "../../components/GoogleAdsAccount";
import PhoneSystemSettings from "../../components/PhoneSystemSettings";
import GoogleCalendarSettings from "../../components/GoogleCalendarSettings";
import AttentionSettings from "../../components/AttentionSettings";

import { i18n } from "../../translate/i18n.js";
import { toast } from "../../services/toast";

import useAuth from "../../hooks/useAuth.js";
import useSettings from "../../hooks/useSettings";
import OnlyForSuperUser from "../../components/OnlyForSuperUser";

const useStyles = makeStyles()((theme) => ({
  root: {
    flex: 1,
    backgroundColor: theme.palette.background.paper,
  },
  mainPaper: {
    display: "flex",
    flexDirection: "row",
    flex: 1,
    overflow: "hidden",
    background: theme.palette.background.default,
  },
  tab: {
    flexShrink: 0,
    width: 220,
    background: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    ...theme.scrollbarStyles,
  },
  tabItem: {
    justifyContent: "flex-start",
    textAlign: "left",
    minHeight: 44,
    paddingLeft: theme.spacing(2.5),
    "& .MuiTab-icon": {
      marginRight: theme.spacing(1.25),
    },
  },
  paper: {
    ...theme.scrollbarStyles,
    overflowY: "auto",
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "stretch",
    width: "100%",
    flex: 1,
  },
  container: {
    width: "100%",
    maxHeight: "100%",
  },
  control: {
    padding: theme.spacing(1),
  },
  textfield: {
    width: "100%",
  },
}));

const VALID_TABS = [
  "options",
  "attention",
  "contactStatus",
  "openai",
  "metaAds",
  "googleAds",
  "asterisk",
  "google-calendar",
  "companies",
  "plans",
  "helps",
];

const SettingsCustom = () => {
  const { classes } = useStyles();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");
  const [tab, setTab] = useState(
    VALID_TABS.includes(initialTab) ? initialTab : "options"
  );
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState({});

  const { getCurrentUserInfo } = useAuth();
  const { getAll: getAllSettings } = useSettings();

  const loadData = async () => {
    try {
      const [settingList, user] = await Promise.all([getAllSettings(), getCurrentUserInfo()]);
      setSettings(settingList);
      setCurrentUser(user);
    } catch (e) {
      toast.error(e);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, newValue) => {
    loadData();
    setTab(newValue);
  };

  const isSuper = () => {
    return currentUser?.super ?? false;
  };

  return (
    <MainContainer className={classes.root} maxWidth={false}>
      <MainHeader>
        <Title>{i18n.t("settings.title")}</Title>
      </MainHeader>
      <Paper className={classes.mainPaper} elevation={1}>
        <Tabs
          value={tab}
          orientation="vertical"
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          onChange={handleTabChange}
          className={classes.tab}
        >
          <Tab className={classes.tabItem} icon={<TuneOutlined fontSize="small" />} iconPosition="start" label={i18n.t("settings.tabs.options")} value={"options"} />
          <Tab className={classes.tabItem} icon={<SupportAgentOutlined fontSize="small" />} iconPosition="start" label={i18n.t("settings.tabs.attention")} value={"attention"} />
          <Tab className={classes.tabItem} icon={<LabelOutlined fontSize="small" />} iconPosition="start" label={i18n.t("settings.tabs.contactStatus")} value={"contactStatus"} />
          <Tab className={classes.tabItem} icon={<SmartToyOutlined fontSize="small" />} iconPosition="start" label={i18n.t("settings.tabs.openai")} value={"openai"} />
          <Tab className={classes.tabItem} icon={<CampaignOutlined fontSize="small" />} iconPosition="start" label={i18n.t("settings.tabs.metaAds")} value={"metaAds"} />
          <Tab className={classes.tabItem} icon={<AdsClickOutlined fontSize="small" />} iconPosition="start" label={i18n.t("settings.tabs.googleAds")} value={"googleAds"} />
          <Tab className={classes.tabItem} icon={<PhoneInTalkOutlined fontSize="small" />} iconPosition="start" label={i18n.t("settings.tabs.asterisk")} value={"asterisk"} />
          <Tab className={classes.tabItem} icon={<CalendarMonthOutlined fontSize="small" />} iconPosition="start" label={i18n.t("settings.tabs.googleCalendar")} value={"google-calendar"} />
          {isSuper() ? <Tab className={classes.tabItem} icon={<BusinessOutlined fontSize="small" />} iconPosition="start" label={i18n.t("settings.tabs.companies")} value={"companies"} /> : null}
          {isSuper() ? <Tab className={classes.tabItem} icon={<WorkspacePremiumOutlined fontSize="small" />} iconPosition="start" label={i18n.t("settings.tabs.plans")} value={"plans"} /> : null}
          {isSuper() ? <Tab className={classes.tabItem} icon={<HelpOutlineOutlined fontSize="small" />} iconPosition="start" label={i18n.t("settings.tabs.helps")} value={"helps"} /> : null}
          {isSuper() ? <Tab className={classes.tabItem} icon={<HubOutlined fontSize="small" />} iconPosition="start" label={i18n.t("settings.tabs.metaApi")} value={"metaApi"} /> : null}
        </Tabs>
        <Paper className={classes.paper} elevation={0}>
          <OnlyForSuperUser
            user={currentUser}
            yes={() => (
              <TabPanel
                className={classes.container}
                value={tab}
                name={"companies"}
              >
                <CompaniesManager />
              </TabPanel>
            )}
          />
          <OnlyForSuperUser
            user={currentUser}
            yes={() => (
              <TabPanel
                className={classes.container}
                value={tab}
                name={"plans"}
              >
                <PlansManager />
              </TabPanel>
            )}
          />
          <OnlyForSuperUser
            user={currentUser}
            yes={() => (
              <TabPanel
                className={classes.container}
                value={tab}
                name={"helps"}
              >
                <HelpsManager />
              </TabPanel>
            )}
          />
          <OnlyForSuperUser
            user={currentUser}
            yes={() => (
              <TabPanel className={classes.container} value={tab} name={"metaApi"}>
                <MetaGraphVersionSettings />
              </TabPanel>
            )}
          />
          <TabPanel className={classes.container} value={tab} name={"attention"}>
            <AttentionSettings />
          </TabPanel>
          <TabPanel className={classes.container} value={tab} name={"contactStatus"}>
            <ContactStatusManager />
          </TabPanel>
          <TabPanel className={classes.container} value={tab} name={"openai"}>
            <OpenAiAccountSettings />
          </TabPanel>
          <TabPanel className={classes.container} value={tab} name={"metaAds"}>
            <MetaAdsAccountSettings />
          </TabPanel>
          <TabPanel className={classes.container} value={tab} name={"googleAds"}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <GoogleAdsAccount />
              <GoogleAdsSettings />
              <GoogleAdsLinks />
            </Box>
          </TabPanel>
          <TabPanel className={classes.container} value={tab} name={"asterisk"}>
            <PhoneSystemSettings />
          </TabPanel>
          <TabPanel className={classes.container} value={tab} name={"google-calendar"}>
            <GoogleCalendarSettings />
          </TabPanel>
          <TabPanel className={classes.container} value={tab} name={"options"}>
            <Options settings={settings} />
          </TabPanel>
        </Paper>
      </Paper>
    </MainContainer>
  );
};

export default SettingsCustom;
